import React, { useEffect, useState, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import PersonIcon from '@mui/icons-material/Person';
import { getCookie, setCookie } from '../../auth/handleCookie';
import { useGetAccountsQuery } from '../../api/recalc/endpoints/accountsApiSlice';
import { getHostname } from '../../utils/getHostname';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedAccount, getSelectedAccount } from '../../redux/selectedAccountSlice';
import { setAllAccounts, getAllAccounts } from '../../redux/allAccountsSlice';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';

import Popper from "@mui/material/Popper";
import { styled } from "@mui/material/styles";

// Custom Popper for dynamic dropdown width
const StyledPopper = styled(Popper)(({ theme }) => ({
  width: "auto", // Allow the dropdown to adapt to content width
  maxWidth: "none", // Remove any imposed width restrictions
  zIndex: theme.zIndex.modal, // Ensure proper z-index stacking
}));

import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';

const extractAccountIdFromGuiState = (guiState) => {
    const params = new URLSearchParams(guiState?.replace(/&/g, '&'));
    return params.get('AccountId');
};

const updateGuiStateCookie = (newAccountId) => {
    const guiState = getCookie(`${getHostname()}_GuiState`);
    const params = new URLSearchParams(guiState?.replace(/&/g, '&'));
    params.set('AccountId', newAccountId);
    const newGuiState = Array.from(params.entries()).map(([key, value]) => `${key}=${value}`).join('&');
    setCookie(`${getHostname()}_GuiState`, newGuiState, { path: '/' });
};

const AccountSelect = () => {
    const { data: accounts, isSuccess: accountsIsSuccess, isError: accountsIsError, error: accountsError } = useGetAccountsQuery();
    const [currentAccount, setCurrentAccount] = React.useState(null);
    const guiState = getCookie(`${getHostname()}_GuiState`);
    const accountId = extractAccountIdFromGuiState(guiState);
    const selectedAccount = useSelector(getSelectedAccount);

    const textWidthRef = useRef(null); // Reference to measure the text width
    const [inputWidth, setInputWidth] = useState(150); // Default width for the input

    const handleClick = () => {
        dispatch(setSelectedAccount(accounts[0]));
        setCurrentAccount(accounts[0]);
    }

    const dispatch = useDispatch();

    const { t } = useTranslation('glossary');

    useEffect(() => {
        if (accountsIsSuccess && accounts) {
            const initialAccount = accounts.find(account => account.AccountId.toString() === accountId);
            setCurrentAccount(initialAccount ?? accounts[0]);
            dispatch(setAllAccounts(accounts));
            dispatch(setSelectedAccount(initialAccount ?? accounts[0]));
        }
    }, [accounts, accountsIsSuccess]);

    useEffect(() => {
        if (selectedAccount.AccountId !== currentAccount?.AccountId) {
            setCurrentAccount(selectedAccount);
            updateGuiStateCookie(selectedAccount.AccountId);
        }
    }, [selectedAccount]);

    useEffect(() => {
        if (selectedAccount) {
            // Measure the width of the selected option
            const width = textWidthRef.current?.offsetWidth || 150;
            setInputWidth(Math.max(width + 150, 150)); // Add padding, set minimum width
        }
    }, [selectedAccount]);

    const filterOptions = (options, { inputValue }) => {
        return options?.filter(
            option =>
            option.Name.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.AccountId.toString().startsWith(inputValue)
        );
    };

    const handleAccountSelect = (event, value) => {
        setCurrentAccount(value);
        if (value) {
            dispatch(setSelectedAccount(value));
            updateGuiStateCookie(value.AccountId);
            // openDrawer(false);
        }
    };

    return (
        <>
            <span ref={textWidthRef} style={{ visibility: 'hidden', position: 'absolute', whiteSpace: 'nowrap' }}>
                {selectedAccount?.Name || ""}
            </span>
            <Autocomplete
                disablePortal
                className='element'
                width='auto'
                options={accounts || []}
                PopperComponent={StyledPopper} // Use custom Popper
                value={selectedAccount?.AccountId ? selectedAccount : null}
                componentsProps={{ popper: { style: { width: 'fit-content' } } }}
                filterOptions={filterOptions}
                onChange={handleAccountSelect}
                getOptionLabel={(option) => (option?.Name || "").toString()}
                renderOption={(props, option) => (
                    <li {...props} key={option.AccountId}>
                        {option.Name}
                    </li>
                )}
                sx={{
                    width: inputWidth, // Dynamically update width
                    maxWidth: '100%', // Ensure it doesn't overflow
                    mr: 1,
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        // label={t('glossary:account')}
                        variant='outlined'
                        size='small'
                        sx={{
                            '& .MuiInputBase-root': {
                                borderRadius: '10px', // Customize border radius here
                            },
                            '& .MuiInputBase-root:hover': {
                                borderRadius: '10px', // Customize border radius here
                                borderColor: 'divider'
                            },
                            '& .MuiFilledInput-root': {
                                borderRadius: '5px', // Customize border radius for filled variant
                            },
                            '& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after': {
                                display: 'none', // Remove underline
                            },
                            '& .MuiInputLabel-root': {
                                color: 'gray',
                            },
                            '& .MuiOutlinedInput-root': {
                            borderRadius: '10px', // Customize border radius
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'divider', // Change border color on hover
                            },
                            },
                            // '& .MuiOutlinedInput-notchedOutline': {
                            // borderColor: 'gray', // Default border color
                            // },
                            '& .MuiInputLabel-root': {
                            color: 'gray',
                            },
                        }}
                        InputLabelProps={{
                            shrink: true, // Always keep the label in the "shrunken" state
                        }}
                        InputProps={{
                            ...params.InputProps,
                            //disableUnderline: true,
                            placeholder: t('glossary:account'), // Use a placeholder
                            startAdornment: (
                                <InputAdornment position='start'>
                                    {/* {'is_admin' in tokenDecoded ? (
                                        <IconButton size="small" sx={{ borderRadius: 2 }} onClick={handleClick}>
                                            <AdminPanelSettingsRoundedIcon sx={{ color: 'gray' }} />
                                        </IconButton>
                                    ) : (
                                        <PersonIcon sx={{ color: 'gray' }} />
                                    )
                                    } */}
                                    {true ? (
                                        <IconButton size="small" sx={{ borderRadius: 2 }} onClick={handleClick}>
                                            <AdminPanelSettingsRoundedIcon sx={{ color: 'gray' }} />
                                        </IconButton>
                                    ) : (
                                        <PersonIcon sx={{ color: 'gray' }} />
                                    )
                                    }
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            />
        </>
    );
};

export default AccountSelect;